import {useState} from 'react';
import {useOfficeAction} from '../hooks/useOfficeAction';
import {PickupReferences} from './po-office';
// Materials due vs schedule. Due = the schedule day that uses the material,
// never the job's next date. Until S11 (inventory) exists the lines are derived
// from the job's product, area and stairs against the install days; the mock
// overlays the risk it knows about.
import { Link } from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {useT} from "../lib/i18n";
import {money} from "../lib/format";
import {loadPurchaseOrders} from "../lib/purchase-orders";
import type { InstallDay, Job, Material } from "../hooks/slice-a";
import { day } from "../lib/format";
import { Pill, type PillTone } from "./job-bits";

const PACK_M2 = 2.2;
const WASTE = 1.08;

/** Example lines from what the job knows. Packs = m² × 8 % waste at 2.2 m²/pack. */
export function deriveMaterials(job: Pick<Job, "ref" | "product" | "area_m2" | "stairs">, days: InstallDay[]): Material[] {
  const sorted = days.slice().sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0]?.date ?? null;
  const stairDay = sorted.find((d) => /stair/i.test(d.task ?? ""))?.date ?? sorted[sorted.length - 1]?.date ?? null;
  const dayNo = (iso: string | null) => (iso ? sorted.findIndex((d) => d.date === iso) + 1 : 0);
  const poRef = `PO-${job.ref.slice(-4)}`;
  const out: Material[] = [];
  if (job.product && job.area_m2) {
    const carpet = /carpet|twist|signature/i.test(job.product);
    const packs = Math.ceil((job.area_m2 * WASTE) / PACK_M2);
    out.push({
      item: job.product, detail: "all floored rooms",
      qty: carpet ? `${Math.ceil((job.area_m2 * WASTE) / 3.66)} lm · 3.66 m roll` : `${packs} pk`,
      source: poRef, due: first, due_note: first ? `day ${dayNo(first)} · first ${carpet ? "roll" : "planks"}` : "no day booked yet",
      status: first ? "on order" : "not ordered", status_note: first ? "ETA meets the day" : "waits for a booked day",
    });
    out.push({ item: carpet ? "Underlay · 10 mm foam" : "Underlay 2 mm", detail: carpet ? "under the carpet" : "under the planks", qty: `${Math.ceil(job.area_m2 / 20)} rolls · ${Math.ceil(job.area_m2 / 20) * 20} m²`, source: poRef, due: first, due_note: first ? `day ${dayNo(first)}` : "no day booked yet", status: first ? "on order" : "not ordered", status_note: "same truck as the floor" });
    if (!carpet) out.push({ item: "Scotia · colour match · 2.4 m", detail: "skirting to remain", qty: `${Math.ceil(job.area_m2 / 12)}`, source: poRef, due: first, due_note: first ? `day ${dayNo(first)}` : "no day booked yet", status: first ? "on order" : "not ordered", status_note: "with the floor" });
  }
  if (job.stairs) {
    out.push({ item: "Stair nosing · alu · 3 m", detail: `${job.stairs} steps · cut per step`, qty: String(job.stairs), source: "Stock", due: stairDay, due_note: stairDay ? `day ${dayNo(stairDay)} · stairs` : "no day booked yet", status: "allocated", status_note: `${job.stairs} of 26 on hand` });
  }
  return out;
}

const TONE: Record<Material["status"], PillTone> = { "at risk": "warn", confirmed: "ok", allocated: "info", "not ordered": "warn", "on order": "plain" };

export function MaterialsTable({ rows }: { rows: Material[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No product or area on the job yet, so nothing to order.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
        <tr><th className="pb-1 font-medium">Item</th><th className="pb-1 text-right font-medium">Qty</th><th className="hidden pb-1 font-medium sm:table-cell">Source</th><th className="pb-1 font-medium">Due</th><th className="pb-1 font-medium">Status</th></tr>
      </thead>
      <tbody>
        {rows.map((m) => {
          const risk = m.status === "at risk" || m.status === "not ordered";
          return (
            <tr key={m.item} className="border-t align-top">
              <td className="py-2 pr-2"><div className="font-medium">{m.item}</div><div className="text-micro text-muted-foreground">{m.detail}</div></td>
              <td className="num py-2 pr-2 text-right whitespace-nowrap">{m.qty}</td>
              <td className="hidden py-2 pr-2 font-mono text-micro sm:table-cell">{m.source === "Stock" ? <span className="font-sans text-sm">Stock</span> : <Link className="text-primary" to="/suppliers">{m.source}</Link>}</td>
              <td className="py-2 pr-2 whitespace-nowrap"><div className={risk ? "font-semibold" : ""}>{day(m.due)}</div><div className="text-micro text-muted-foreground">{m.due_note}</div></td>
              <td className="py-2"><Pill tone={TONE[m.status]}>{m.status}</Pill><div className="text-micro text-muted-foreground">{m.status_note}</div></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * The live half of "Materials due vs schedule" (card AIW-PO).
 *
 * The panel used to say material status "is not connected here yet" and send staff
 * to the old platform. This reads the job's purchase orders and says what is true:
 * what was ordered, when it went, whether anything came back, and which day it has
 * to land before. Nothing here invents a promised date the legacy PO does not carry.
 */
/** Jacky, 4 October 2026: a draft that was never sent can be withdrawn here, with its reason (cancel_po_draft). */
function CancelDraft({poId}: {poId: string}) {
  const {t} = useT();
  const action = useOfficeAction();
  const [open, setOpen] = useState(false), [reason, setReason] = useState('');
  if (!open) return <button type="button" className="mt-1 text-micro text-primary underline-offset-2 hover:underline" onClick={() => setOpen(true)}>{t('po.cancelDraft')}</button>;
  return <form className="mt-2 flex flex-wrap items-center gap-2 text-micro" onSubmit={(e) => {e.preventDefault(); if (reason.trim().length >= 3) void action.run('cancel_po_draft', {po_id: poId, reason: reason.trim()});}}>
    <input className="w-72 rounded border bg-background px-2 py-1 text-sm" placeholder={t('po.cancelDraftReason')} value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3}/>
    <button type="submit" disabled={action.busy || reason.trim().length < 3} className="rounded border px-2 py-1 disabled:opacity-50">{t('po.cancelDraftConfirm')}</button>
    <button type="button" className="px-2 py-1 text-muted-foreground" onClick={() => setOpen(false)}>{t('job.profit.cancel')}</button>
    {action.notice && <span role="status">{action.notice}</span>}
  </form>;
}

/** Jacky, 5 October 2026: "this order … is actually sent but need a real cancel". Marks it cancelled where it lives
 * and drafts the cancellation email to the supplier, which waits on Communications for one approve click. */
export function CancelWithSupplier({source, poKey, supplier}: {source: 'crm' | 'platform'; poKey: string; supplier: string | null}) {
  const {t} = useT();
  const action = useOfficeAction();
  const [open, setOpen] = useState(false), [reason, setReason] = useState(''), [done, setDone] = useState(false);
  if (done) return <p role="status" className="mt-1 text-micro">{t('po.cancelSentDone', {supplier: supplier ?? '—'})} <Link className="text-primary underline" to="/communications">{t('po.cancelSentOpenComms')}</Link></p>;
  if (!open) return <button type="button" className="mt-1 text-micro text-primary underline-offset-2 hover:underline" onClick={() => setOpen(true)}>{t('po.cancelSent')}</button>;
  return <form className="mt-2 flex flex-wrap items-center gap-2 text-micro" onSubmit={(e) => {e.preventDefault(); if (reason.trim().length >= 3) void action.run('cancel_sent_po', {source, po_key: poKey, reason: reason.trim()}).then((r) => { if ((r as {status?: string} | undefined)?.status === 'executed') setDone(true); });}}>
    <span className="w-full text-muted-foreground">{t('po.cancelSentHelp', {supplier: supplier ?? '—'})}</span>
    <input className="w-72 rounded border bg-background px-2 py-1 text-sm" placeholder={t('po.cancelSentReason')} value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3}/>
    <button type="submit" disabled={action.busy || reason.trim().length < 3} className="rounded border border-warning px-2 py-1 text-warning disabled:opacity-50">{t('po.cancelSentConfirm')}</button>
    <button type="button" className="px-2 py-1 text-muted-foreground" onClick={() => setOpen(false)}>{t('job.profit.cancel')}</button>
    {action.notice && <span role="status">{action.notice}</span>}
  </form>;
}

export function PurchaseOrderMaterials({jobRef}: {jobRef: string}) {
  const {t} = useT();
  const q = useQuery({queryKey: ['job-purchase-orders', jobRef], queryFn: () => loadPurchaseOrders({jobRef, status: 'all'}), refetchInterval: 60_000});
  if (q.isPending) return <p role="status" className="text-sm">{t('po.loading')}</p>;
  if (q.isError) return <p role="alert" className="text-sm">{t('po.unavailable', {message: (q.error as Error).message})}</p>;
  const rows = q.data ?? [];
  if (!rows.length) return <p className="text-sm text-muted-foreground">{t('job.materials.none')}</p>;
  return <ul className="space-y-2">
    {rows.map((r) => <li key={`${r.source}:${r.po}`} className={`rounded-md border p-3 text-sm ${r.material_must_land ? 'border-l-4 border-l-warning' : ''}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link className="font-mono text-xs text-primary underline" to={`/purchase-orders/${encodeURIComponent(r.key??r.po)}`}>{r.po}</Link>
        <span className="text-micro text-muted-foreground">{r.supplier ?? '—'} · {r.lines} lines · <span className="text-gold">{money(r.total_ex_gst)}</span> ex GST</span>
      </div>
      <p className="mt-1 text-micro text-muted-foreground">
        {t('po.detail.sent')} {r.sent_at ? day(r.sent_at) : t('po.notRecorded')} · {t('po.detail.acknowledged')} {r.acknowledged_at ? day(r.acknowledged_at)! : t('po.notRecorded')} · {t('po.detail.delivered')} {r.delivered_at ? day(r.delivered_at)! : t('po.notRecorded')}
      </p>
      <PickupReferences row={r}/>
      {r.source === 'crm' && ['draft', 'approved'].includes(r.status.toLowerCase()) && <CancelDraft poId={r.key}/>}
      {['sent', 'acknowledged'].includes(r.status.toLowerCase()) && <CancelWithSupplier source={r.source} poKey={r.key} supplier={r.supplier}/>}
      {r.material_must_land && <p className="mt-1 text-sm text-warning"><b>{t('po.mustLand')}</b> — {t('po.mustLandNote', {sent: r.sent_at ? day(r.sent_at)! : t('po.notRecorded'), install: r.next_install ? day(r.next_install)! : '—'})}</p>}
    </li>)}
  </ul>;
}
